import { Stack } from 'component-library';
import { useTranslation } from 'react-i18next';

import { CustomBenchmarkSuccessResponse } from '@/api/rest/resources/types/benchmark';
import { PlotReportFactElement } from '@/api/rest/resources/types/fact';
import { GraphTileType } from '@/api/rest/resources/types/widget';
import { CarbonStorageBGHistoricalChart } from '@/components/Charts';
import { ChartHeading } from '@/components/Charts/components/ChartHeading';
import { useChartZoom } from '@/components/Charts/components/ChartZoom';
import { GraphFact } from '@/components/Charts/types';
import { getChartDataWithBenchmarks, kgToTonneFormatter } from '@/components/Charts/utils';
import { useNCCardContext } from '@/components/NCCard/NCCard';
import { getBenchmarksFromFacts } from '@/pages/shared/hooks/useBenchmarkForFact';

import { NoPlotData } from '../NoPlotData';

export const CarbonGraphTile = ({
  graphData,
  benchmarkFact,
  activeCustomBenchmark,
  selectedBenchmarkId,
  onBenchmarkSelect,
}: {
  graphData: GraphFact[] | null | undefined;
  benchmarkFact: {
    high: PlotReportFactElement<number | null> | undefined;
    moderate: PlotReportFactElement<number | null> | undefined;
    low: PlotReportFactElement<number | null> | undefined;
  };
  activeCustomBenchmark?: CustomBenchmarkSuccessResponse | null;
  selectedBenchmarkId: string | null;
  onBenchmarkSelect: (id: string | null) => void;
}) => {
  const { t } = useTranslation();
  const { analysisType } = useNCCardContext();

  const { zoom, setZoom } = useChartZoom();

  const cbgBenchmarks = getBenchmarksFromFacts({
    highBenchmarkFact: benchmarkFact.high,
    moderateBenchmarkFact: benchmarkFact.moderate,
    lowBenchmarkFact: benchmarkFact.low,
    formatter: kgToTonneFormatter,
  });

  const noChartData = !graphData?.length;

  if (noChartData) {
    return <NoPlotData />;
  }

  const chartData = getChartDataWithBenchmarks({
    chartData: graphData,
    benchmarks: benchmarkFact,
  });

  return (
    <Stack
      spacing={6}
      className='items-center justify-center px-1 py-6 lg:items-start lg:p-6 lg:pl-1'
      data-testid='carbon-graph-tile'
    >
      <ChartHeading
        heading={t('shared.ncaDetail.details.carbon.labels.historicalGraphHeadline')}
        popover={{
          // TODO: MVP-3284 Confirm if the popover copy is correct
          title: t('shared.ncaDetail.details.carbon.explainers.belowTheGroundCarbonStorage.title'),
          body: t('shared.ncaDetail.details.carbon.explainers.belowTheGroundCarbonStorage.body'),
        }}
        className='w-full px-6 sm:px-0'
        zoom={zoom}
        setZoom={setZoom}
        indicator={GraphTileType.historic_below_ground_carbon}
        selectedBenchmark={selectedBenchmarkId}
        setSelectedBenchmark={onBenchmarkSelect}
      />
      <CarbonStorageBGHistoricalChart
        analysisType={analysisType}
        data={chartData}
        benchmarks={cbgBenchmarks}
        customBenchmark={activeCustomBenchmark}
        showTooltip={true}
        className='relative'
        data-testid='carbon-storage-bg-chart'
        zoom={zoom}
        setZoom={setZoom}
      />
    </Stack>
  );
};
